import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useMutation, useQueryClient } from 'react-query';
import { CerebroTextButton } from '../../shared/components';
import { useApi } from '../../shared/context';
import { QueryKeys } from '../../shared/hooks';
import { CurrentUser, DataRoomDiligenceRequest, DiligenceRequestFile } from '../../shared/models';
import { CerebroApiEndpoints } from '../../shared/utils/api';
import { getResponseData } from '../../shared/utils/axios';

interface DeleteRequestFileDialogProps {
	dataRoomGuid: string | undefined;
	request: DataRoomDiligenceRequest | null;
	file: DiligenceRequestFile | null;
	currentUser: CurrentUser;
	close: () => void;
}

export const DeleteRequestFileDialog: React.FC<DeleteRequestFileDialogProps> = ({
	dataRoomGuid,
	request,
	file,
	currentUser,
	close,
}) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const client = useQueryClient();

	const onRemoveConfirmation = () => {
		if (request !== null) removeRequest(request);
	};

	const { mutate: removeRequest } = useMutation<void, unknown, DataRoomDiligenceRequest>(
		() =>
			getResponseData(
				api.delete<void>(
					CerebroApiEndpoints.DataRoomDiligenceRequests.SpecificFile(dataRoomGuid, request?.guid, file?.guid)
				)
			),
		{
			onSuccess: () => {
				if (currentUser.isBankUser) {
					client.invalidateQueries(QueryKeys.DataRoomDiligenceRequests.Lender(dataRoomGuid));
				} else {
					client.invalidateQueries(QueryKeys.DataRoomDiligenceRequests.Root(dataRoomGuid));
				}

				snackbar.enqueueSnackbar('Diligence Request File Deleted.', { variant: 'success' });
				close();
			},
			onError: () => {
				snackbar.enqueueSnackbar('There was a problem deleting the diligence request file.', {
					variant: 'error',
				});
				close();
			},
		}
	);

	return (
		<Dialog open={file !== null} onClose={close}>
			<DialogTitle>Confirm Delete</DialogTitle>
			<DialogContent>
				<DialogContentText>
					Are you sure you want to delete this diligence request file? This will not delete the document from
					the document repository.
				</DialogContentText>
			</DialogContent>
			<DialogActions>
				<CerebroTextButton onClick={close}>No</CerebroTextButton>
				<CerebroTextButton onClick={onRemoveConfirmation}>Yes</CerebroTextButton>
			</DialogActions>
		</Dialog>
	);
};
